import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'
import remarkGfm from 'remark-gfm'
import { KRAKEN_PRIMARY } from './KrakenChatBar'

export interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
}

interface KrakenChatMessageProps {
  message: string
  isUser: boolean
  isStreaming?: boolean
  onSendMessage?: (message: string) => void
}

const MotionBox = motion(Box)

const EntityLink = ({ type, id, name, onClick }: { type: string; id: string; name?: string; onClick: () => void }) => {
  const isSuggestion = type === 'suggestion'
  const displayName = isSuggestion ? id : (name || id.replace(/^(seller_|item_|saw_)/, ''))
  const shortId = id.replace(/^(seller_|item_|saw_)/, '').slice(0, 8)

  if (isSuggestion) {
    return (
      <Text
        as="span"
        color="purple.600"
        fontWeight={800}
        cursor="pointer"
        bg="purple.50"
        px={1.5}
        py={0.5}
        borderRadius="4px"
        borderColor="purple.300"
        _hover={{ borderColor: 'purple.500', bg: 'purple.100' }}
        transition="all 0.15s ease"
        onClick={onClick}
        title={`Send: ${name ?? id}`}
      >
        {displayName}
      </Text>
    )
  }

  return (
    <Text
      as="span"
      color={KRAKEN_PRIMARY}
      fontWeight={500}
      cursor="pointer"
      borderBottom="1px dashed"
      borderColor="purple.300"
      _hover={{ borderColor: KRAKEN_PRIMARY, bg: 'purple.50' }}
      transition="all 0.15s ease"
      onClick={onClick}
      title={`View ${type}: ${id}`}
    >
      {name ? displayName : `${type} ${shortId}...`}
    </Text>
  )
}

const parseEntityLinks = (text: string, onEntityClick: (type: string, id: string, name?: string) => void): React.ReactNode[] => {
  const parts: React.ReactNode[] = []
  const regex = /\[\[(\w+):([^\]|]+)(?:\|([^\]]+))?\]\]/g
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const [, type, id, name] = match
    parts.push(
      <EntityLink
        key={`${id}-${match.index}`}
        type={type}
        id={id}
        name={name}
        onClick={() => onEntityClick(type, id, name)}
      />,
    )

    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

const ThinkingChain = ({ steps }: { steps: string[] }) => {
  return (
    <Box mb={3} position="relative" pl={1}>
      {/* Vertical connecting line through bullet centers */}
      {steps.length > 1 && (
        <Box
          position="absolute"
          left="7.5px"
          top="14px"
          bottom="14px"
          w="1px"
          transform="translateX(-50%)"
          bg="neutral.200"
          opacity={0.5}
        />
      )}

      <VStack align="stretch" gap={0}>
        {steps.map((step, index) => {
          const isActive = index === steps.length - 1
          const isCompleted = index < steps.length - 1

          return (
            <MotionBox
              key={index}
              display="flex"
              alignItems="center"
              gap={2.5}
              h="28px"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.05 }}
            >
              {/* Bullet — fixed size, vertically centered */}
              <Box
                w="7px"
                h="7px"
                borderRadius="full"
                bg={isActive ? 'neutral.500' : 'neutral.300'}
                opacity={isCompleted ? 0.4 : 1}
                flexShrink={0}
                zIndex={1}
              />

              <HStack gap={1.5} align="center">
                <Text
                  fontSize="13px"
                  color={isActive ? 'neutral.600' : 'neutral.400'}
                  fontWeight={isActive ? 500 : 400}
                  lineHeight="1"
                  css={
                    isActive
                      ? {
                          animation: 'breatheText 2.5s ease-in-out infinite',
                          '@keyframes breatheText': {
                            '0%, 100%': { opacity: 0.6 },
                            '50%': { opacity: 1 },
                          },
                        }
                      : undefined
                  }
                >
                  {step}
                </Text>

                {isActive && (
                  <HStack gap="2px" ml={0.5}>
                    {[0, 1, 2].map((i) => (
                      <MotionBox
                        key={i}
                        w="3px"
                        h="3px"
                        borderRadius="full"
                        bg="neutral.400"
                        animate={{
                          opacity: [0.2, 0.7, 0.2],
                          scale: [0.8, 1.1, 0.8],
                        }}
                        transition={{
                          duration: 1.4,
                          repeat: Infinity,
                          delay: i * 0.2,
                          ease: 'easeInOut',
                        }}
                      />
                    ))}
                  </HStack>
                )}
              </HStack>
            </MotionBox>
          )
        })}
      </VStack>
    </Box>
  )
}

const StreamingText = ({
  content,
  isStreaming,
  components,
}: {
  content: string
  isStreaming: boolean
  components: any
}) => {
  return (
    <Box position="relative">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
      {isStreaming && content && (
        <MotionBox
          as="span"
          display="inline-block"
          w="2px"
          h="14px"
          bg="neutral.400"
          ml={0.5}
          verticalAlign="text-bottom"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}
    </Box>
  )
}

const KrakenChatMessage = ({ message, isUser, isStreaming = false, onSendMessage }: KrakenChatMessageProps) => {
  const navigate = useNavigate()
  const [thinkingSteps, setThinkingSteps] = useState<string[]>([])
  const [displayContent, setDisplayContent] = useState('')
  const [isThinking, setIsThinking] = useState(false)

  const handleEntityClick = useCallback(
    (type: string, id: string, name?: string) => {
      const cleanId = id.replace(/^(seller_|item_|saw_)/, '')

      switch (type.toLowerCase()) {
        case 'suggestion':
          onSendMessage?.(name ?? id)
          break
        case 'seller':
          navigate(`/react/sellers/${cleanId}`)
          break
        case 'item':
          navigate(`/react/items/${cleanId}`)
          break
        case 'saw':
        case 'website':
          navigate(`/react/saw/sites/${cleanId}`)
          break
        default:
          console.warn(`Unknown entity type: ${type}`)
      }
    },
    [navigate, onSendMessage],
  )

  const processChildren = useCallback(
    (children: React.ReactNode): React.ReactNode => {
      if (typeof children === 'string') {
        return parseEntityLinks(children, handleEntityClick)
      }
      if (Array.isArray(children)) {
        return children.map((child, i) =>
          typeof child === 'string' ? <span key={i}>{parseEntityLinks(child, handleEntityClick)}</span> : child,
        )
      }
      return children
    },
    [handleEntityClick],
  )

  useEffect(() => {
    if (isUser) {
      setDisplayContent(message)
      setThinkingSteps([])
      setIsThinking(false)
      return
    }

    const isThinkingMessage =
      message?.includes('Fetching') ||
      message?.includes('Analyzing') ||
      message?.includes('Processing') ||
      message?.includes('Loading') ||
      message?.includes('Searching') ||
      message?.includes('Scraping') ||
      message?.includes('Investigating') ||
      message?.includes('Querying') ||
      message?.includes('Capturing') ||
      message?.includes('Identifying') ||
      message?.includes('Finding')

    if (isStreaming && !message) {
      setIsThinking(true)
      setThinkingSteps(['Thinking'])
      setDisplayContent('')
    } else if (isStreaming && isThinkingMessage) {
      setIsThinking(true)
      const cleanMessage = message.replace(/^[🔍🧠⚙️💭]\s*/, '')
      setThinkingSteps((prev) => {
        if (prev[prev.length - 1] === cleanMessage) return prev
        return [...prev, cleanMessage]
      })
      setDisplayContent('')
    } else if (message) {
      setIsThinking(false)
      setDisplayContent(message)
    }
  }, [isStreaming, message, isUser])

  const markdownComponents = {
    p: ({ children }: any) => (
      <Text mb={2} mt={1} color="neutral.800" lineHeight="1.6">
        {processChildren(children)}
      </Text>
    ),
    strong: ({ children }: any) => (
      <Text as="strong" fontWeight="600" color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    em: ({ children }: any) => (
      <Text as="em" fontStyle="italic" color="neutral.700">
        {processChildren(children)}
      </Text>
    ),
    ul: ({ children }: any) => (
      <Box as="ul" pl={5} mb={2}>
        {children}
      </Box>
    ),
    ol: ({ children }: any) => (
      <Box as="ol" pl={5} mb={2}>
        {children}
      </Box>
    ),
    li: ({ children }: any) => (
      <Text as="li" mb={1} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    h1: ({ children }: any) => (
      <Text as="h1" fontSize="xl" fontWeight="600" mb={2} mt={3} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    h2: ({ children }: any) => (
      <Text as="h2" fontSize="lg" fontWeight="600" mb={2} mt={3} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    h3: ({ children }: any) => (
      <Text as="h3" fontSize="md" fontWeight="600" mb={1.5} mt={2} color="neutral.800">
        {processChildren(children)}
      </Text>
    ),
    table: ({ children }: any) => (
      <Box overflowX="auto" my={3} borderRadius="8px" border="1px solid" borderColor="neutral.200">
        <Box as="table" w="full" fontSize="13px" css={{ borderCollapse: 'collapse' }}>
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }: any) => (
      <Box as="thead" bg="neutral.50">
        {children}
      </Box>
    ),
    tbody: ({ children }: any) => <Box as="tbody">{children}</Box>,
    tr: ({ children }: any) => (
      <Box as="tr" borderBottom="1px solid" borderColor="neutral.100" _last={{ borderBottom: 'none' }}>
        {children}
      </Box>
    ),
    th: ({ children }: any) => (
      <Box as="th" px={3} py={2} fontWeight="600" color="neutral.700" fontSize="12px" textAlign="left">
        {processChildren(children)}
      </Box>
    ),
    td: ({ children }: any) => (
      <Box as="td" px={3} py={2} color="neutral.800" verticalAlign="top">
        {processChildren(children)}
      </Box>
    ),
    img: ({ src, alt }: any) => (
      <Box my={2} borderRadius="8px" overflow="hidden" border="1px solid" borderColor="neutral.200" maxW="100%">
        <Box
          as="img"
          src={src}
          alt={alt || ''}
          maxW="100%"
          maxH="400px"
          objectFit="contain"
          display="block"
          bg="neutral.50"
        />
        {alt && (
          <Text fontSize="11px" color="neutral.500" px={2} py={1} bg="neutral.50">
            {alt}
          </Text>
        )}
      </Box>
    ),
    a: ({ href, children }: any) => (
      <Text
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        color={KRAKEN_PRIMARY}
        fontWeight={500}
        borderBottom="1px dashed"
        borderColor="purple.300"
        _hover={{ borderColor: KRAKEN_PRIMARY, bg: 'purple.50' }}
        transition="all 0.15s ease"
      >
        {processChildren(children)}
      </Text>
    ),
    code: ({ children, className }: any) => {
      const isInline = !className
      if (isInline) {
        return (
          <Text
            as="code"
            px={1.5}
            py={0.5}
            bg="neutral.100"
            borderRadius="4px"
            fontSize="13px"
            fontFamily="mono"
            color="neutral.700"
          >
            {children}
          </Text>
        )
      }
      return (
        <Box as="pre" p={3} bg="neutral.800" borderRadius="8px" overflowX="auto" my={2}>
          <Text as="code" fontSize="13px" fontFamily="mono" color="white">
            {children}
          </Text>
        </Box>
      )
    },
  }

  return (
    <VStack align="stretch" gap={1.5} role="group" w="full">
      <Box w="full" display="flex" justifyContent={isUser ? 'flex-end' : 'flex-start'} position="relative">
        <Box maxW={!isUser ? '100%' : '70%'}>
          {isUser ? (
            <Box
              px={4}
              py={3}
              borderRadius="20px"
              bg="secondary.400"
              color="white"
              fontSize="14px"
              lineHeight="1.5"
              fontWeight={500}
            >
              <Text fontWeight={500}>{displayContent}</Text>
            </Box>
          ) : (
            <Box color="neutral.800" fontSize="14px" lineHeight="1.6" fontWeight={400}>
              <AnimatePresence mode="wait">
                {isThinking && thinkingSteps.length > 0 ? (
                  <ThinkingChain key="thinking" steps={thinkingSteps} />
                ) : displayContent ? (
                  <MotionBox
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <StreamingText content={displayContent} isStreaming={isStreaming} components={markdownComponents} />
                  </MotionBox>
                ) : null}
              </AnimatePresence>
            </Box>
          )}
        </Box>
      </Box>
    </VStack>
  )
}

export default KrakenChatMessage
